/**
 * Shared formatting utilities.
 *
 * Single source of truth for time-ago strings, counts, durations, and file
 * sizes so every surface stays consistent.
 */

// ─── Time-ago ────────────────────────────────────────────────────────────────

interface TimeAgoOptions {
  /** Append " ago" to relative strings (e.g. "5m ago" instead of "5m"). */
  suffix?: boolean;
  /**
   * After this many days fall back to `Date.toLocaleDateString()`.
   * Omit or set to 0 to never fall back.
   */
  dateAfterDays?: number;
  /** Options passed to `toLocaleDateString` when the date fallback is used. */
  dateFormatOptions?: Intl.DateTimeFormatOptions;
}

/**
 * Human-readable relative timestamp.
 *
 * Accepts both ISO-8601 strings (client components) and `Date` objects
 * (server API routes).
 *
 * @example
 *   formatTimeAgo("2026-04-09T12:00:00Z")                     // "5m"
 *   formatTimeAgo(someDate, { suffix: true })                  // "5m ago"
 *   formatTimeAgo(someDate, { suffix: true, dateAfterDays: 7 }) // "Apr 3, 2026"
 */
export function formatTimeAgo(
  input: string | Date | null | undefined,
  options: TimeAgoOptions = {},
): string {
  if (!input) return "";
  const { suffix = false, dateAfterDays = 0, dateFormatOptions } = options;
  const date = typeof input === "string" ? new Date(input) : input;
  if (isNaN(date.getTime())) return "";
  const diff = Date.now() - date.getTime();
  const secs = Math.floor(diff / 1000);
  const ago = suffix ? " ago" : "";

  if (secs < 60) return "just now";

  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m${ago}`;

  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h${ago}`;

  const days = Math.floor(hrs / 24);

  if (dateAfterDays > 0 && days >= dateAfterDays) {
    return date.toLocaleDateString(
      "en-US",
      dateFormatOptions ?? { month: "short", day: "numeric", year: "numeric" },
    );
  }

  if (days >= 7 && !dateAfterDays) {
    const weeks = Math.floor(days / 7);
    return `${weeks}w${ago}`;
  }

  return `${days}d${ago}`;
}

// ─── Numeric formatting ──────────────────────────────────────────────────────

/** Compact count: 1200 → "1K", 1500000 → "1.5M" */
export function formatCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${Math.floor(n / 1_000)}K`;
  return String(n);
}

// ─── Duration / file size ────────────────────────────────────────────────────

/** Formats seconds → "m:ss" or "h:mm:ss" */
export function formatDuration(seconds: number): string {
  const s = Math.round(seconds);
  const hours = Math.floor(s / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const remaining = s % 60;

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, "0")}:${String(remaining).padStart(2, "0")}`;
  }
  return `${minutes}:${String(remaining).padStart(2, "0")}`;
}

/**
 * Journey age from a day count.
 * Under a month: "Day 14". After 30 days: "5 months 4 days".
 * After a year: "1 year 2 months 10 days". Zero units are omitted.
 */
export function formatJourneyDuration(days: number): string {
  const total = Math.max(0, Math.floor(Number(days) || 0));
  if (total <= 30) return `Day ${total}`;

  const years = Math.floor(total / 365);
  const afterYears = total - years * 365;
  const months = Math.floor(afterYears / 30);
  const remDays = afterYears - months * 30;

  const parts: string[] = [];
  if (years > 0) parts.push(years === 1 ? "1 year" : `${years} years`);
  if (months > 0) parts.push(months === 1 ? "1 month" : `${months} months`);
  if (remDays > 0) parts.push(remDays === 1 ? "1 day" : `${remDays} days`);
  return parts.join(" ") || `Day ${total}`;
}

/** Spoken duration: "5m 30s", "45s" — for summaries and human-facing text */
export function formatDurationHuman(seconds: number): string {
  const s = Math.round(seconds);
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return m > 0 ? `${m}m ${rem}s` : `${rem}s`;
}

/** Formats bytes → "1.5 MB" */
export function formatFileSize(bytes: number): string {
  const sizes = ["Bytes", "KB", "MB", "GB"];
  if (bytes === 0) return "0 Bytes";
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${Math.round((bytes / Math.pow(1024, i)) * 100) / 100} ${sizes[i]}`;
}

// ─── Currency ────────────────────────────────────────────────────────────────

/** Formats a number as USD currency string. */
export function formatCurrency(amount: number | string): string {
  const numericAmount =
    typeof amount === "string" ? parseFloat(amount) || 0 : amount || 0;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numericAmount);
}

/** `datetime-local` has no timezone — treat the value as the user's local clock. */
export function datetimeLocalToIso(value: string): string {
  const m = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/);
  if (m) {
    return new Date(
      Number(m[1]),
      Number(m[2]) - 1,
      Number(m[3]),
      Number(m[4]),
      Number(m[5]),
      m[6] ? Number(m[6]) : 0,
    ).toISOString();
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toISOString();
}

export function toDatetimeLocalValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
